import { nicknameProblem, normalizeNickname, type Member } from '@hive/shared';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription } from '@/components/ui/dialog';
import { Field, Input } from '@/components/ui/field';
import { useUpdateProfile } from '@/data/members';
import { PushDevices } from './PushDevices';

/** Your name and nickname (people can @mention or assign you by either), and which devices get notifications. */
export function AccountSettings({ member, onClose }: { member: Member; onClose: () => void }) {
  const update = useUpdateProfile();
  const [name, setName] = useState(member.display_name);
  const [nickname, setNickname] = useState(member.nickname ?? '');
  const nick = normalizeNickname(nickname);
  const problem = nicknameProblem(nick);
  const firstName = name.trim().split(/\s+/)[0] ?? '';
  const handles = [...new Set([firstName, nick].filter(Boolean))];

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent title="Account settings">
        <DialogDescription className="text-sm text-muted">
          Your name shows on messages, tasks and docs. Pick a nickname too, and people can @mention or assign you by
          either.
        </DialogDescription>
        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (problem || !name.trim()) return;
            update.mutate(
              { id: member.id, patch: { display_name: name.trim(), nickname: nick || null } },
              { onSuccess: onClose },
            );
          }}
        >
          <Field label="Name">
            <Input required value={name} maxLength={80} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label="Nickname (optional)">
            <div className="flex items-center gap-1">
              <span className="font-mono text-sm text-dim">@</span>
              <Input
                value={nickname}
                aria-label="Nickname"
                placeholder="edweird"
                maxLength={31}
                aria-invalid={!!problem}
                onChange={(e) => setNickname(e.target.value)}
              />
            </div>
          </Field>
          {problem ? (
            <p role="alert" className="-mt-2 font-mono text-[11px] text-red">
              {problem}
            </p>
          ) : handles.length ? (
            <p className="-mt-2 font-mono text-[11px] text-dim">
              People can mention you as {handles.map((h) => `@${h}`).join(' or ')}.
            </p>
          ) : null}
          <p className="font-mono text-[11px] text-dim">Signed in as {member.email}</p>
          {update.error ? (
            <p role="alert" className="font-mono text-xs text-red">
              {update.error.message}
            </p>
          ) : null}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!!problem || !name.trim() || update.isPending}>
              Save
            </Button>
          </div>
        </form>
        <PushDevices />
      </DialogContent>
    </Dialog>
  );
}
